import { z } from "zod";
import {
  doctorMessageTypeSchema,
  meetingNoticeCtaSchema,
  patientMeetingNoticeDetailSchema,
} from "./meeting-notice";

// TES-1120 (TES-1112 S4, Emelie's point 10, approved by Kirill):
// "icke-akut medicinsk fråga skapar ett spårbart ärende i vårdteamets arbetskö; Kirills svar publiceras som ett nytt medicinskt besked under Din vård."
//
// The carrier is the MedicalReviewCase (TES-986). A coach's "Skicka till
// läkare" and a Support escalation to the doctor (TES-1110) each open one;
// the doctor answers it with "Svara som medicinskt besked", which publishes
// a MedicalNoticeVersion of kind `medical_question_answer` and resolves the
// case. The decision and its reasons are on TES-1120 (2026-10-06).

// The category of a medical question. The same list staff choose from when
// they escalate from Ella Support (TES-1110); a coach picks one too. Kept here
// rather than in support-escalation.ts so handoff.ts can use it without an
// import cycle (support-escalation imports handoff).
export const MEDICAL_QUESTION_CATEGORIES = [
  "symptoms",
  "side_effects",
  "medication",
  "lab_results",
  "treatment",
  "lifestyle",
  "other",
] as const;
export const medicalQuestionCategorySchema = z.enum(
  MEDICAL_QUESTION_CATEGORIES,
);
export type MedicalQuestionCategory = z.infer<
  typeof medicalQuestionCategorySchema
>;

export const MEDICAL_QUESTION_CATEGORY_LABELS: Record<
  MedicalQuestionCategory,
  string
> = {
  symptoms: "Symtom eller mående",
  side_effects: "Biverkningar",
  medication: "Läkemedel eller dosering",
  lab_results: "Provsvar",
  treatment: "Behandling eller recept",
  lifestyle: "Kost, träning eller vanor",
  other: "Annat",
};

// Ö-341 (Emelie 2026-10-05): "Vanligtvis inom två vardagar" applies to a
// medical question from the coach or Support, "counted from when it is
// escalated to the care team's medical queue". It does not change the
// coach's own response time. Working days are Monday to Friday in
// Europe/Stockholm minus Swedish public holidays and the de facto
// non-working eves (Ö-348, TES-1136; apps/api medical-review/
// swedish-holidays.ts).
export const MEDICAL_QUESTION_SLA_WORKING_DAYS = 2;
export const MEDICAL_QUESTION_TIME_ZONE = "Europe/Stockholm";

// Ö-333 (Emelie 2026-10-05), verbatim. "Kan användas av både coach och Ella
// Support när en fråga faktiskt har eskalerats" — so it is offered only on an
// escalation to the doctor, never on its own, posted in the thread the
// question came from (as Ella Support, or as the coach).
export const MEDICAL_ESCALATION_STANDARD_REPLY =
  "Tack, din medicinska fråga har skickats vidare till vårdteamet. Du får en notifiering när ett medicinskt besked finns under Din vård. Vi svarar vanligtvis inom två vardagar. Vid akuta eller snabbt förvärrade besvär ska du inte invänta svar från Ella. Ring 112 vid livshotande tillstånd eller kontakta 1177 för sjukvårdsrådgivning.";

// Emelie's point 10, verbatim: the notification and Home card when a doctor
// has published a medical notice. `carebyella://health/care` is Din vård; in
// app 2.0.4 and 2.0.5 it has no notices list yet (S5, app 2.0.6).
export const MEDICAL_NOTICE_PUBLISHED_COPY = {
  title: "Du har fått ett nytt besked från läkaren",
  body: "Läs beskedet under Din vård.",
  ctaLabel: "Läs läkarens besked",
  ctaPath: "carebyella://health/care",
} as const;

// TES-1136 (Ö-351, Emelie 2026-10-06): no fixed number of days. The Home
// card stays until she has OPENED the notice, then it goes; the notice
// itself always stays under Din vård. "An unread medical notice must never
// disappear just because 7 days have passed." App 2.0.6 records the open
// (POST patient-api/v1/medical-notices/:id/read); for 2.0.4/2.0.5, see
// home-now.service.ts.
export const medicalNoticeReadResponseSchema = z.object({
  /** The first time she opened this notice version; a repeat keeps it. */
  readAt: z.string(),
});
export type MedicalNoticeReadResponse = z.infer<
  typeof medicalNoticeReadResponseSchema
>;

// TES-1136 (Ö-350, Emelie 2026-10-06), verbatim: the last line of the
// answer's copy in the doctor chat (MEDICAL_NOTICE_CHAT_BRIDGE).
export const MEDICAL_NOTICE_CHAT_BRIDGE_LINE =
  "Det här beskedet finns även under Din vård.";

export const MEDICAL_ANSWER_BODY_MAX = 4000;
// The same limit as CUSTOM_COMPLETION_HEADING_MAX (completion-picker.ts);
// not imported, because that module's imports reach chat.ts, which imports
// this one. medical-question.spec.ts holds the two equal.
export const MEDICAL_ANSWER_TASK_HEADING_MAX = 120;

// TES-1332 (TES-1331, Emelie 2026-10-13) — "Besked till patienten" has
// exactly two modes, chosen with a radio in admin:
//   - "Besked – inget svar behövs": the notice only (no `question`);
//   - "Besked med fråga – patienten ska svara": the notice and one bounded
//     question ("Din fråga"), answered once in words under Din vård. No
//     file upload in this version.
// The generic "Patienten behöver göra något — skapa en uppgift i appen" and
// its fields (heading, instruction, mark-as-done, upload) are gone from this
// flow: a request that still carries `task` is refused, never silently
// stripped, so a stale admin tab cannot lose what the doctor wrote. Open
// tasks created before TES-1332 keep working (custom-completion-task.ts).
export const NOTICE_MODES = ["info", "question"] as const;
export type NoticeMode = (typeof NOTICE_MODES)[number];

export const DOCTOR_QUESTION_MAX = 2000;
export const noticeQuestionSchema = z.object({
  text: z.string().trim().min(1).max(DOCTOR_QUESTION_MAX),
});

// "Svara som medicinskt besked" — the answer to a patient's medical
// question, in either mode.
export const medicalQuestionAnswerRequestSchema = z.object({
  body: z.string().trim().min(1).max(MEDICAL_ANSWER_BODY_MAX),
  question: noticeQuestionSchema.optional(),
  task: z.undefined({ message: "task_removed" }).optional(),
});
export type MedicalQuestionAnswerRequest = z.input<
  typeof medicalQuestionAnswerRequestSchema
>;

export const medicalQuestionAnswerResponseSchema = z.object({
  caseId: z.string(),
  notice: z.object({ id: z.string(), version: z.number().int().positive() }),
  // The question's id when the notice asks one (CustomCompletionTask).
  taskId: z.string().nullable(),
});
export type MedicalQuestionAnswerResponse = z.infer<
  typeof medicalQuestionAnswerResponseSchema
>;

// TES-1147 — "Besked till patienten": a notice the doctor sends on his own
// initiative, with no medical question behind it, in either mode.
//
// TES-1332 — `followUpOf` is "Skicka nytt besked" from the work queue's "Svar
// på läkarens fråga": the answered question this notice follows up. The
// answer is marked handled with the new notice in the same transaction; the
// earlier question and her answer stay unchanged.
//
// TES-1393 — `type` is the composer's choice of message (09C 2026-10-13):
// the medical notice (default, as before) or one of the two meeting
// messages. A meeting message has no question and follows nothing up ("Det
// finns ingen svarsruta"); `cta` is "Om ditt vårdmöte"'s optional CTA, and
// "Förslag om extra vårdmöte" always carries "Boka vårdmöte".
export const DOCTOR_NOTICE_REQUEST_ERRORS = {
  meetingQuestion: "meeting_notice_question_not_allowed",
  meetingFollowUp: "meeting_notice_follow_up_not_allowed",
  // TES-1637 — a follow-up from the monthly case is a question.
  monthlyCaseNeedsQuestion: "monthly_case_follow_up_needs_question",
  ctaOnNotice: "cta_only_on_meeting_messages",
  proposalCta: "meeting_proposal_cta_is_book_meeting",
} as const;
export const doctorNoticeRequestSchema = medicalQuestionAnswerRequestSchema
  .extend({
    followUpOf: z.object({ taskId: z.string().uuid() }).optional(),
    type: doctorMessageTypeSchema.optional(),
    cta: meetingNoticeCtaSchema.optional(),
    // TES-1637 — "ställ en följdfråga" from the monthly case: the question
    // belongs to that case (the prescription cycle under review), which
    // reads "Inväntar patientsvar" until she answers.
    monthlyCase: z.object({ cycleId: z.string().uuid() }).optional(),
  })
  .superRefine((v, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
    const type = v.type ?? "doctor_notice";
    if (v.monthlyCase && (type !== "doctor_notice" || !v.question)) {
      issue("monthlyCase", DOCTOR_NOTICE_REQUEST_ERRORS.monthlyCaseNeedsQuestion);
    }
    if (type === "doctor_notice") {
      if (v.cta) issue("cta", DOCTOR_NOTICE_REQUEST_ERRORS.ctaOnNotice);
      return;
    }
    if (v.question)
      issue("question", DOCTOR_NOTICE_REQUEST_ERRORS.meetingQuestion);
    if (v.followUpOf)
      issue("followUpOf", DOCTOR_NOTICE_REQUEST_ERRORS.meetingFollowUp);
    if (type === "meeting_proposal" && v.cta && v.cta !== "book_meeting")
      issue("cta", DOCTOR_NOTICE_REQUEST_ERRORS.proposalCta);
  });
export type DoctorNoticeRequest = z.input<typeof doctorNoticeRequestSchema>;

// TES-1332 — the copy of the two modes, verbatim from the 2026-10-13
// decisions (Drive: app 1C3CwG7C…, admin 16-jIN979…).
export const NOTICE_QUESTION_COPY = {
  // The Home task while her answer is outstanding.
  homeTitle: "Din läkare har skickat en fråga",
  homeCtaLabel: "Läs och svara",
  // Under Din vård, below the notice.
  heading: "Fråga från din läkare",
  answerCta: "Skicka svar",
  answeredConfirmation:
    "Ditt svar är skickat till läkaren. Du får en notifiering om läkaren skickar ett nytt besked.",
  // TES-1335 — a withdrawn question under Din vård. Proposed copy: the
  // decision names the withdrawal, not its wording.
  withdrawn: "Läkaren har återkallat frågan. Du behöver inte svara.",
  // Admin: the two modes, and the "no reply" line on the info mode.
  modeInfoLabel: "Besked – inget svar behövs",
  modeQuestionLabel: "Besked med fråga – patienten ska svara",
  questionLabel: "Din fråga",
  cannotReply: "Patienten kan inte svara direkt på detta besked",
} as const;

// TES-1332 — the Home task's destination: the notice itself under Din vård,
// where the question and its answer field are. Built in code, never
// authored, so it is not one of DEEP_LINK_PARAM_PREFIXES; served only to a
// build that resolves it (CLIENT_CONTRACT_CHANGES.noticeQuestion).
export const MEDICAL_NOTICE_DEEP_LINK_PREFIX =
  "carebyella://health/care/notice/";
export function medicalNoticeDeepLink(noticeId: string): string {
  return `${MEDICAL_NOTICE_DEEP_LINK_PREFIX}${noticeId}`;
}

// TES-1149 — the row's heading in her CP-06 checklist (app 2.0.7–2.0.8,
// which show the question there), and the queue item's title when she has
// answered. TES-1332: the heading is the decision's "Fråga från din läkare".
export const DOCTOR_QUESTION_HEADING = NOTICE_QUESTION_COPY.heading;
export const DOCTOR_QUESTION_ANSWERED_TITLE = "Svar på läkarens fråga";

// TES-1149 / TES-1332 — a doctor's question as staff read it, next to the
// notice it was sent with. Statuses, verbatim: "Inväntar patientsvar",
// "Besvarad", "Hanterad".
// TES-1335 — "withdrawn": the doctor withdrew it before she answered
// ("läkaren uttryckligen återkallar frågan"). Its label is proposed copy.
export const doctorQuestionStatusSchema = z.enum([
  "waiting",
  "answered",
  "handled",
  "withdrawn",
]);
export type DoctorQuestionStatus = z.infer<typeof doctorQuestionStatusSchema>;
export const DOCTOR_QUESTION_STATUS_LABELS: Record<
  DoctorQuestionStatus,
  string
> = {
  waiting: "Inväntar patientsvar",
  answered: "Besvarad",
  handled: "Hanterad",
  withdrawn: "Återkallad",
};
// TES-1335 — `medical_action`: "Starta medicinsk åtgärd", a structured
// action took the answer over (DoctorQuestionSummary.handledAction).
export const DOCTOR_QUESTION_HANDLED_VIA = [
  "marked_handled",
  "new_notice",
  "medical_action",
] as const;
export const doctorQuestionHandledViaSchema = z.enum(
  DOCTOR_QUESTION_HANDLED_VIA,
);
export type DoctorQuestionHandledVia = z.infer<
  typeof doctorQuestionHandledViaSchema
>;

// TES-1335 (admin decision 2026-10-13, "ARBETSKÖ FÖR SVAR PÅ LÄKARENS
// FRÅGA", action 3) — "Starta medicinsk åtgärd": "När svaret kräver ett
// strukturerat vårdflöde ska arbetskön erbjuda en explicit direktlänk till
// rätt funktion". The decision's five examples, labels verbatim. Creating
// the chosen action closes the answer ("startar en strukturerad medicinsk
// åtgärd som övertar ärendet") with the action recorded on it, so the
// question, her answer and the action that took over stay linked.
// TES-1400 (Ö-576): choosing it alone closes nothing.
export const DOCTOR_QUESTION_MEDICAL_ACTIONS = [
  "request_completion",
  "propose_meeting",
  "prescription_review",
  "treatment_plan",
  "register_medical_case",
] as const;
export const doctorQuestionMedicalActionSchema = z.enum(
  DOCTOR_QUESTION_MEDICAL_ACTIONS,
);
export type DoctorQuestionMedicalAction = z.infer<
  typeof doctorQuestionMedicalActionSchema
>;
export const DOCTOR_QUESTION_MEDICAL_ACTION_LABELS: Record<
  DoctorQuestionMedicalAction,
  string
> = {
  request_completion: "Begär komplettering",
  propose_meeting: "Föreslå vårdmöte",
  prescription_review: "Öppna receptbedömning",
  treatment_plan: "Öppna behandlingsplan",
  register_medical_case: "Registrera medicinskt ärende",
};
export const doctorQuestionMedicalActionRequestSchema = z.object({
  action: doctorQuestionMedicalActionSchema,
});
export type DoctorQuestionMedicalActionRequest = z.infer<
  typeof doctorQuestionMedicalActionRequestSchema
>;

// TES-1335 — "Om patientens svar innehåller en säkerhetssignal ska den
// beslutade säkerhetsvägen visas före normal hantering." No rule marks
// patient-written free text as a safety signal yet (the same as for a
// medical question through Kontakta Ella), so the server always sends null
// and nothing is shown. Once a rule is decided, a non-null signal puts its
// path above the row's actions and the server refuses "Hanterat" while it
// stands. `label` is what the doctor is shown.
export const doctorQuestionSafetySignalSchema = z.object({
  label: z.string(),
});
export type DoctorQuestionSafetySignal = z.infer<
  typeof doctorQuestionSafetySignalSchema
>;
export const doctorQuestionSummarySchema = z.object({
  taskId: z.string(),
  // The notice it was sent with; null if that notice cannot be found.
  noticeId: z.string().nullable(),
  noticeBody: z.string().nullable(),
  // TES-1332 — when that notice was published.
  noticePublishedAt: z.string().nullable(),
  question: z.string(),
  uploadAllowed: z.boolean(),
  askedAt: z.string(),
  // The asking doctor's name, or null if the account is gone.
  askedBy: z.string().nullable(),
  status: doctorQuestionStatusSchema,
  answer: z.string().nullable(),
  answeredAt: z.string().nullable(),
  // TES-1332 — the doctor's handling of her answer. `handledVia` is null for
  // an answer closed through the queue's generic close before TES-1332.
  handledAt: z.string().nullable(),
  handledBy: z.string().nullable(),
  handledVia: doctorQuestionHandledViaSchema.nullable(),
  followUpNoticeId: z.string().nullable(),
  // TES-1335 — the structured action that took the answer over, when
  // `handledVia` is medical_action.
  handledAction: doctorQuestionMedicalActionSchema.nullable(),
  // TES-1335 — when and by whom it was withdrawn (status "withdrawn").
  withdrawnAt: z.string().nullable(),
  withdrawnBy: z.string().nullable(),
  // TES-1335 — always null until a safety-signal rule is decided.
  safetySignal: doctorQuestionSafetySignalSchema.nullable(),
  // Files she attached to the row (the optional upload, before TES-1332).
  uploads: z.number().int().nonnegative(),
  // TES-1637 — the monthly case (prescription cycle) the question was asked
  // in, or null. Optional so an older API response still parses.
  cycleId: z.string().nullable().optional(),
});
export type DoctorQuestionSummary = z.infer<typeof doctorQuestionSummarySchema>;
export const doctorQuestionListSchema = z.object({
  questions: z.array(doctorQuestionSummarySchema),
});
export type DoctorQuestionList = z.infer<typeof doctorQuestionListSchema>;

// TES-1332 — "Hanterat – inget nytt besked behövs" from the queue row.
export const doctorQuestionHandledResponseSchema = z.object({
  handledAt: z.string(),
  // False when it was already handled; nothing new was recorded.
  changed: z.boolean(),
});
export type DoctorQuestionHandledResponse = z.infer<
  typeof doctorQuestionHandledResponseSchema
>;

// TES-1335 — "Återkalla frågan" on a question she has not answered.
export const doctorQuestionWithdrawnResponseSchema = z.object({
  withdrawnAt: z.string(),
  // False when it was already withdrawn; nothing new was recorded.
  changed: z.boolean(),
});
export type DoctorQuestionWithdrawnResponse = z.infer<
  typeof doctorQuestionWithdrawnResponseSchema
>;

export const doctorNoticeResponseSchema = z.object({
  notice: z.object({ id: z.string(), version: z.number().int().positive() }),
  taskId: z.string().nullable(),
});
export type DoctorNoticeResponse = z.infer<typeof doctorNoticeResponseSchema>;

// The coach's standard reply after her own "Skicka till läkare": a separate
// call naming the handoff it follows, so it can only ever come after an
// actual escalation.
export const medicalEscalationReplyRequestSchema = z.object({
  handoffId: z.string().uuid(),
});
export type MedicalEscalationReplyRequest = z.infer<
  typeof medicalEscalationReplyRequestSchema
>;
export const medicalEscalationReplyResponseSchema = z.object({
  messageId: z.string(),
  // True when the reply had already been posted for this escalation; nothing
  // new was sent.
  alreadySent: z.boolean(),
});
export type MedicalEscalationReplyResponse = z.infer<
  typeof medicalEscalationReplyResponseSchema
>;

// The patient's read of her published medical notices (S5's Din vård list
// in app 2.0.6). Only kinds whose body is text written for her are served;
// the decision kinds store structured JSON and get a patient rendering in S5.
export const PATIENT_READABLE_MEDICAL_NOTICE_KINDS = [
  "medical_question_answer",
  // TES-1147 — app 2.0.6 renders a notice without looking at its kind, so a
  // second text kind needs no app release.
  "doctor_notice",
  // TES-1393 — the meeting messages. Served with their own kind only to a
  // build at or after CLIENT_CONTRACT_CHANGES.meetingNotices, and as
  // `doctor_notice` to every other one (servedNoticeKind).
  "meeting_info",
  "meeting_proposal",
] as const;
export type PatientReadableMedicalNoticeKind =
  (typeof PATIENT_READABLE_MEDICAL_NOTICE_KINDS)[number];

/**
 * TES-1393 — the kind a build is told. A build before
 * CLIENT_CONTRACT_CHANGES.meetingNotices knows no meeting kind, and reads a
 * meeting message as the doctor's notice it was before.
 */
export function servedNoticeKind(
  kind: PatientReadableMedicalNoticeKind,
  meetingNotices: boolean,
): PatientReadableMedicalNoticeKind {
  if (meetingNotices) return kind;
  return kind === "meeting_info" || kind === "meeting_proposal"
    ? "doctor_notice"
    : kind;
}

export const patientMedicalNoticeSchema = z.object({
  id: z.string(),
  kind: z.enum(PATIENT_READABLE_MEDICAL_NOTICE_KINDS),
  // 1 for the first publication; a later correction of the same answer is a
  // new version and replaces it in the list.
  version: z.number().int().positive(),
  body: z.string(),
  publishedAt: z.string(),
  // The signing doctor's name, or null if the account is gone.
  publishedBy: z.string().nullable(),
  // TES-1332 — "Besked med fråga": the doctor's question published with
  // this notice, and her answer once sent (read only, with its time). Absent
  // or null on "Besked – inget svar behövs". `id` is the question's
  // CustomCompletionTask id: she answers it through POST
  // patient-api/v1/medical-evidence/custom-tasks/:id/answer, the route app
  // 2.0.7 already uses, so the answer lands in the same queue whichever
  // screen she sent it from. Optional: app 2.0.6–2.0.8 ignore it, and read
  // the question in the CP-06 checklist instead.
  //
  // TES-1335 — `withdrawnAt`: the doctor withdrew the question before she
  // answered it; she reads it as withdrawn and cannot answer. Sent only to
  // a build that renders it (CLIENT_CONTRACT_CHANGES.noticeQuestionWithdrawn);
  // every other build is served the notice without the withdrawn question.
  question: z
    .object({
      id: z.string(),
      text: z.string(),
      answer: z.object({ text: z.string(), answeredAt: z.string() }).nullable(),
      withdrawnAt: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  // TES-1393 — a meeting message's CTA and booking state: only on the two
  // meeting kinds, and only for a build at or after
  // CLIENT_CONTRACT_CHANGES.meetingNotices.
  meeting: patientMeetingNoticeDetailSchema.optional(),
});
export type PatientMedicalNotice = z.infer<typeof patientMedicalNoticeSchema>;
export type PatientNoticeQuestion = NonNullable<
  PatientMedicalNotice["question"]
>;

// TES-1130 (S5) — where she takes a question about a notice, which picks
// 09C's closing line under it: "coach" while she has a personal coach,
// otherwise "support" (Kontakta Ella). Read from her coach access when she
// reads, not when the notice was published: a notice outlives a plan change,
// and the line must point at a door that is open today.
export const MEDICAL_NOTICE_ASK_VIA = ["coach", "support"] as const;
export const medicalNoticeAskViaSchema = z.enum(MEDICAL_NOTICE_ASK_VIA);
export type MedicalNoticeAskVia = z.infer<typeof medicalNoticeAskViaSchema>;

export const patientMedicalNoticeViewSchema = patientMedicalNoticeSchema.extend(
  { askVia: medicalNoticeAskViaSchema },
);
export type PatientMedicalNoticeView = z.infer<
  typeof patientMedicalNoticeViewSchema
>;

export const patientMedicalNoticeListSchema = z.object({
  notices: z.array(patientMedicalNoticeViewSchema),
  // TES-1147 — her askVia also when she has no notices, for app 2.0.7's
  // doctor-history pointer (coach or Kontakta Ella). Optional: a server
  // before TES-1147 sends it only on each notice.
  askVia: medicalNoticeAskViaSchema.optional(),
});
export type PatientMedicalNoticeList = z.infer<
  typeof patientMedicalNoticeListSchema
>;
